<template>
  <div>
    <Nav></Nav>

    <b-container fluid>
      <b-row align-v="center" class="mb-2">
        <b-col>
          <h2 class="mb-0">Menus</h2>
          <p class="text-muted mb-0">Crea menús para tus plataformas de delivery y configúralos con categorías, productos y modificadores.</p>
        </b-col>
        <b-col cols="auto">
          <b-button variant="success" @click="openCreateModal">
            <b-icon-plus></b-icon-plus> Crear Menu
          </b-button>
        </b-col>
      </b-row>

      <!-- Empty state / call to action -->
      <b-row v-if="!isLoading && menus.length === 0">
        <b-col>
          <b-card class="text-center py-5">
            <b-icon-card-list font-scale="3" variant="secondary"></b-icon-card-list>
            <h4 class="mt-3">Aún no tienes menús</h4>
            <p class="text-muted">Crea tu primer menú para empezar a publicar productos en tus plataformas.</p>
            <b-button variant="success" @click="openCreateModal">
              <b-icon-plus></b-icon-plus> Crear mi primer menú
            </b-button>
          </b-card>
        </b-col>
      </b-row>

      <template v-else>
        <b-row>
          <b-col>
            <b-form-group label="Buscar:">
              <b-form-input type="search" v-model="search" debounce="500" placeholder="Buscar por nombre o descripción..."></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-table
              small
              responsive
              striped
              hover
              :items="menus"
              :fields="tableFields"
              :filter="search"
              :busy="isLoading">
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Cargando...</strong>
                </div>
              </template>
              <template #cell(is_active)="data">
                <b-badge variant="success" v-if="data.item.is_active">Activo</b-badge>
                <b-badge variant="danger" v-else>Inactivo</b-badge>
              </template>
              <template #cell(buttons)="data">
                <b-button-group vertical>
                  <b-button size="sm" variant="primary" @click="configureMenu(data.item)">Configurar</b-button>
                  <b-button size="sm" variant="warning" @click="editMenu(data.item)">Editar</b-button>
                  <b-button size="sm" variant="success" @click="sendMenu(data.item)">Enviar</b-button>
                  <b-button size="sm" variant="info" @click="viewSchedules(data.item.id)">Horarios</b-button>
                  <b-button size="sm" variant="danger" @click="confirmDelete(data.item.id)">Eliminar</b-button>
                </b-button-group>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </template>
    </b-container>

    <!-- Create/Edit Menu Modal -->
    <b-modal
      id="modal-createPlatformMenu"
      :title="isEditing ? 'Editar Menu' : 'Crear Menu'"
      :ok-title="isEditing ? 'Guardar cambios' : 'Crear menú'"
      cancel-title="Cancelar"
      ok-variant="success"
      :ok-disabled="!nameValidation || isSubmitting"
      :busy="isSubmitting"
      @ok.prevent="submitMenu"
      @hidden="resetMenu">
      <b-alert v-if="!isEditing" show variant="light" class="border">
        <small class="text-muted">
          <strong>Pasos para armar tu menú:</strong>
          1) Crea el menú &rarr; 2) Agrega categorías &rarr; 3) Agrega productos &rarr; 4) Configura modificadores &rarr; 5) Envíalo a las plataformas.
          Al crearlo te llevaremos directo al siguiente paso.
        </small>
      </b-alert>
      <form ref="menuForm" @submit.stop.prevent="submitMenu">
        <b-form-group
          label="Nombre:"
          label-for="name-input"
          :state="nameValidation"
          invalid-feedback="El nombre es requerido">
          <b-form-input
            id="name-input"
            ref="nameInput"
            v-model="currentMenu.name"
            :state="nameValidation"
            placeholder="Ej. Menú Comida Rápida"
            required
            @keyup.enter="submitMenu"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Descripción:">
          <b-form-textarea
            v-model="currentMenu.description"
            placeholder="Describe el propósito de este menú"
            rows="3"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="currentMenu.is_active">
            Menu Activo
          </b-form-checkbox>
          <small class="text-muted d-block">Solo los menús activos se pueden enviar a las plataformas.</small>
        </b-form-group>
      </form>
    </b-modal>

    <!-- Post-creation guided next step -->
    <b-modal
      id="modal-menuCreated"
      title="¡Menú creado!"
      ok-title="Configurar ahora"
      ok-variant="success"
      cancel-title="Más tarde"
      centered
      @ok="configureMenu(createdMenu)">
      <p>El menú <strong>{{ createdMenu.name }}</strong> se creó correctamente.</p>
      <p class="mb-0">¿Quieres empezar a agregarle categorías y productos ahora?</p>
    </b-modal>

    <!-- Schedules Modal -->
    <b-modal id="modal-menuSchedules" title="Horarios del Menu" size="lg" @hidden="resetScheduleForm">
      <b-row>
        <b-col>
          <h5 class="mb-3">Horarios para: {{ currentMenu.name }}</h5>
        </b-col>
      </b-row>
      
      <b-table 
        small 
        responsive 
        striped 
        hover 
        :items="schedules" 
        :fields="scheduleFields"
        :busy="isLoadingSchedules">
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Cargando horarios...</strong>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="danger" @click="deleteSchedule(data.item.id)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>

      <template #modal-footer>
        <div class="w-100">
          <b-row>
            <b-col md="3">
              <b-form-group label="Día:">
                <b-form-select v-model="newSchedule.day_of_week" :options="daysOfWeek"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hora inicio:">
                <b-form-input v-model="newSchedule.start_time" type="time"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hora fin:">
                <b-form-input v-model="newSchedule.end_time" type="time"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" class="d-flex align-items-end">
              <b-button variant="success" @click="addSchedule" class="mb-3">Agregar Horario</b-button>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end">
            <b-button variant="secondary" @click="$bvModal.hide('modal-menuSchedules')">Cerrar</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../Nav.vue";

export default {
  name: "PlatformMenus",
  components: { Nav },
  data() {
    return {
      menus: [],
      schedules: [],
      isLoading: false,
      isLoadingSchedules: false,
      isEditing: false,
      isSubmitting: false,
      currentMenu: {
        id: "",
        name: "",
        description: "",
        is_active: true
      },
      createdMenu: {
        id: "",
        name: ""
      },
      newSchedule: {
        day_of_week: "monday",
        start_time: "08:00",
        end_time: "22:00"
      },
      search: "",
      tableFields: [
        { key: "name", label: "Nombre", sortable: true },
        { key: "description", label: "Descripción", sortable: true },
        { key: "is_active", label: "Estado", sortable: true },
        { key: "buttons", label: "Acciones" }
      ],
      scheduleFields: [
        { key: "day_of_week", label: "Día", sortable: true, formatter: this.formatDay },
        { key: "start_time", label: "Hora Inicio", sortable: true },
        { key: "end_time", label: "Hora Fin", sortable: true },
        { key: "actions", label: "Acciones" }
      ],
      daysOfWeek: [
        { value: "monday", text: "Lunes" },
        { value: "tuesday", text: "Martes" },
        { value: "wednesday", text: "Miércoles" },
        { value: "thursday", text: "Jueves" },
        { value: "friday", text: "Viernes" },
        { value: "saturday", text: "Sábado" },
        { value: "sunday", text: "Domingo" }
      ]
    };
  },
  computed: {
    nameValidation() {
      return this.currentMenu.name && this.currentMenu.name.trim().length > 0 ? true : false;
    }
  },
  mounted() {
    this.getAllMenus();
  },
  methods: {
    formatDay(value) {
      const dayMap = {
        monday: "Lunes",
        tuesday: "Martes",
        wednesday: "Miércoles",
        thursday: "Jueves",
        friday: "Viernes",
        saturday: "Sábado",
        sunday: "Domingo"
      };
      return dayMap[value] || value;
    },
    getAllMenus() {
      this.isLoading = true;
      axios
        .get("/platform/menus")
        .then(response => {
          this.menus = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener menús", "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    sendMenu(menu) {
      
      this.$bvModal.msgBoxConfirm("¿Estás seguro que deseas enviar este menú a las plataformas asignadas?")
      .then(result => {
        if (result) {
          this.isLoading = true;
          axios
            .post(`/platform/menu/${menu.id}/send`)
            .then(() => {
              this.makeToast("Éxito", "Menú enviado", "success");
              this.getAllMenus();
            })
            .catch(err => {
              this.makeToast(
                "Error", 
                err.response?.data?.message || "Error al enviar el menú", 
                "danger"
              );
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
    },
    openCreateModal() {
      this.resetMenu();
      this.$bvModal.show("modal-createPlatformMenu");
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus();
      });
    },
    editMenu(menu) {
      this.isEditing = true;
      this.currentMenu = { ...menu };
      this.$bvModal.show("modal-createPlatformMenu");
    },
    configureMenu(menu) {
      if (!menu || !menu.id) return;
      this.$router.push({ name: 'platform-builder-by-menu', params: { menu_id: menu.id } });
    },
    resetMenu() {
      this.currentMenu = {
        id: "",
        name: "",
        description: "",
        is_active: true
      };
      this.isEditing = false;
    },
    submitMenu() {
      if (!this.nameValidation || this.isSubmitting) {
        return;
      }

      const wasEditing = this.isEditing;
      const method = wasEditing ? "PUT" : "POST";
      const url = wasEditing
        ? `/platform/menu/${this.currentMenu.id}`
        : '/platform/menu';

      const payload = { ...this.currentMenu, name: this.currentMenu.name.trim() };

      this.isSubmitting = true;
      axios({
        method,
        url,
        data: payload
      })
        .then(response => {
          this.makeToast(
            "Éxito",
            `Menu ${wasEditing ? 'actualizado' : 'creado'} exitosamente`,
            "success"
          );
          this.getAllMenus();
          this.$bvModal.hide("modal-createPlatformMenu");

          // Guide the user into building the newly created menu
          if (!wasEditing && response.data && response.data.id) {
            this.createdMenu = {
              id: response.data.id,
              name: response.data.name || payload.name
            };
            this.$bvModal.show("modal-menuCreated");
          }
        })
        .catch(err => {
          this.makeToast(
            "Error",
            err.response?.data?.message || `Error al ${wasEditing ? 'actualizar' : 'crear'} el menu`,
            "danger"
          );
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    confirmDelete(id) {
      this.$bvModal.msgBoxConfirm("¿Estás seguro que deseas eliminar este menú? Esta acción no se puede deshacer.", {
        title: "Confirmar eliminación",
        okVariant: "danger",
        okTitle: "Eliminar",
        cancelTitle: "Cancelar",
        centered: true
      })
      .then(result => {
        if (result) {
          this.deleteMenu(id);
        }
      });
    },
    deleteMenu(id) {
      axios
        .delete(`/platform/menu/${id}`)
        .then(() => {
          this.makeToast("Éxito", "Menu eliminado exitosamente", "success");
          this.getAllMenus();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al eliminar el menu", 
            "danger"
          );
        });
    },
    viewSchedules(menuId) {
      this.currentMenu.id = menuId;
      const menu = this.menus.find(m => m.id === menuId);
      if (menu) {
        this.currentMenu.name = menu.name;
      }
      this.getSchedules(menuId);
      this.$bvModal.show("modal-menuSchedules");
    },
    getSchedules(menuId) {
      this.isLoadingSchedules = true;
      axios
        .get(`/platform/menu/${menuId}/schedules`)
        .then(response => {
          this.schedules = response.data;
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al obtener horarios", 
            "danger"
          );
        })
        .finally(() => {
          this.isLoadingSchedules = false;
        });
    },
    addSchedule() {
      if (!this.newSchedule.day_of_week || !this.newSchedule.start_time || !this.newSchedule.end_time) {
        this.makeToast("Error", "Todos los campos son requeridos", "danger");
        return;
      }

      axios
        .post(`/platform/menu/${this.currentMenu.id}/schedule`, this.newSchedule)
        .then(() => {
          this.makeToast("Éxito", "Horario agregado exitosamente", "success");
          this.getSchedules(this.currentMenu.id);
          this.resetScheduleForm();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al agregar horario", 
            "danger"
          );
        });
    },
    deleteSchedule(scheduleId) {
      axios
        .delete(`/platform/menu/schedule/${scheduleId}`)
        .then(() => {
          this.makeToast("Éxito", "Horario eliminado exitosamente", "success");
          this.getSchedules(this.currentMenu.id);
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al eliminar horario", 
            "danger"
          );
        });
    },
    resetScheduleForm() {
      this.newSchedule = {
        day_of_week: "monday",
        start_time: "08:00",
        end_time: "22:00"
      };
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>
